import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShoppingBag } from 'lucide-react';
/**
 * Easy to edit: add / remove / change names, locations or message templates here.
 * The component picks a random combination each time a notification appears.
 */
const NAMES = ['Fernando', 'Lucas', 'Martín', 'Juan', 'Nicolás', 'Tomás', 'Santiago', 'Matías', 'Diego', 'Sebastián', 'Federico', 'Agustín', 'Pablo', 'Mariano', 'Joaquín', 'Gonzalo'];
const LOCATIONS = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'Santa Fe', 'La Plata', 'Mar del Plata', 'Tucumán', 'Salta', 'Neuquén', 'San Juan', 'Bahía Blanca'];
const TEMPLATES: Array<(name: string, location: string) => string> = [(n, l) => `${n} de ${l} acaba de comprar este producto`, (n, l) => `${n} de ${l} compró hace pocos minutos`, (n, l) => `${n} de ${l} completó una compra`, (n, l) => `${n} de ${l} compró hoy`, (n, l) => `${n} de ${l} acaba de adquirir la colección`, (n, l) => `${n} de ${l} se sumó hace minutos`, (n, l) => `${n} de ${l} accedió a los 3 manuales`, (n, l) => `${n} de ${l} acaba de completar el pago`];
interface Notification {
  id: number;
  message: string;
}
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const buildNotification = (id: number): Notification => {
  const name = pickRandom(NAMES);
  const location = pickRandom(LOCATIONS);
  const template = pickRandom(TEMPLATES);
  return {
    id,
    message: template(name, location)
  };
};
// Timing config — feel free to tune
const VISIBLE_MS_MIN = 4000;
const VISIBLE_MS_MAX = 6000;
const GAP_MS_MIN = 8000;
const GAP_MS_MAX = 15000;
const FIRST_DELAY_MS = 5000; // wait a few seconds after page load before the first one
export function SocialProof() {
  const [current, setCurrent] = useState<Notification | null>(null);
  useEffect(() => {
    let mounted = true;
    let nextId = 1;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let showTimer: ReturnType<typeof setTimeout> | null = null;
    const randomBetween = (min: number, max: number) => Math.floor(Math.random() * (max - min)) + min;
    const showOne = () => {
      if (!mounted) return;
      setCurrent(buildNotification(nextId++));
      const visibleFor = randomBetween(VISIBLE_MS_MIN, VISIBLE_MS_MAX);
      hideTimer = setTimeout(() => {
        if (!mounted) return;
        setCurrent(null);
        const gap = randomBetween(GAP_MS_MIN, GAP_MS_MAX);
        showTimer = setTimeout(showOne, gap);
      }, visibleFor);
    };
    showTimer = setTimeout(showOne, FIRST_DELAY_MS);
    return () => {
      mounted = false;
      if (hideTimer) clearTimeout(hideTimer);
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);
  return <div className="fixed bottom-4 left-4 z-[60] pointer-events-none max-w-[calc(100vw-2rem)]" data-id="element-230">
      <AnimatePresence mode="wait" data-id="element-231">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        x: -40,
        y: 10
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0
      }} exit={{
        opacity: 0,
        x: -20,
        y: 10
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.4)] px-3 py-3 sm:px-4 sm:py-3 flex items-center gap-3 max-w-xs sm:max-w-sm" data-id="element-232">
            {/* Icon stack */}
            <div className="relative shrink-0" data-id="element-233">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center" data-id="element-234">
                <ShoppingBag className="w-5 h-5 text-emerald-400" data-id="element-235" />
              </div>
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 400,
            damping: 15
          }} className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center ring-2 ring-[#0D1117]" data-id="element-236">
                <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-237" />
              </motion.div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-238">
              <p className="text-white text-xs sm:text-sm font-medium leading-snug" data-id="element-239">
                {current.message}
              </p>
              <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5" data-id="element-240">
                Compra verificada
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}